import { lazy, Suspense, useEffect, useRef, useState, type ComponentType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SpotifyPlayerProvider, useSpotifyPlayerContext } from "@/contexts/SpotifyPlayerContext";
import { PlaylistModalProvider, usePlaylistModal } from "@/contexts/PlaylistModalContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { WebVitalsReporter } from "@/components/WebVitalsReporter";
import { AdminRoute } from "@/components/AdminRoute";
import { StagingGate } from "@/components/StagingGate";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
// SiteImprovementBanner is admin-controlled and rarely active. Lazy-load
// so its module + the deferred supabase poll don't ship in the main
// bundle. Mounts inside the Layout's Suspense fallback boundary above.
const SiteImprovementBanner = lazyWithRetry(() =>
  import("@/components/SiteImprovementBanner").then((m) => ({
    default: m.SiteImprovementBanner,
  })) as Promise<{ default: ComponentType<unknown> }>,
);
import { useServiceWorker } from "@/hooks/useServiceWorker";
import { useServiceWorkerUpdate } from "@/hooks/useServiceWorkerUpdate";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

// Lazy-load non-critical shell components (lazyWithRetry to handle stale chunks after deploys)
const CookieConsent = lazyWithRetry(() => import("@/components/CookieConsent").then(m => ({ default: m.CookieConsent })) as Promise<{ default: ComponentType<unknown> }>);
const AICuratorChatWrapper = lazyWithRetry(() => import("@/components/AICuratorChatWrapper").then(m => ({ default: m.AICuratorChatWrapper })) as Promise<{ default: ComponentType<unknown> }>);
const GlobalSpotifyPlayer = lazyWithRetry(() => import("@/components/GlobalSpotifyPlayer").then(m => ({ default: m.GlobalSpotifyPlayer })) as Promise<{ default: ComponentType<unknown> }>);
const GlobalPlaylistModal = lazyWithRetry(() => import("@/components/GlobalPlaylistModal").then(m => ({ default: m.GlobalPlaylistModal })) as Promise<{ default: ComponentType<unknown> }>);

// Analytics & tracking — lazy because they're not render-critical. Vercel
// Analytics + Speed Insights both fire beacons and inject a tiny pixel; the
// site renders fine without them parsed. ManagedScripts injects admin-
// configured 3rd-party tags (Meta Pixel, Hotjar, etc.) — also non-critical.
// Pulling these out of the synchronous parse path shaves ~10-15KB off the
// main chunk and lets the framework code reach React.render() sooner,
// which is the real LCP gate on a JS-bundle-bound SPA.
const Analytics = lazy(() => import("@vercel/analytics/react").then(m => ({ default: m.Analytics })));
const SpeedInsights = lazy(() => import("@vercel/speed-insights/react").then(m => ({ default: m.SpeedInsights })));
const ManagedScripts = lazyWithRetry(() => import("@/components/ManagedScripts").then(m => ({ default: m.ManagedScripts })) as Promise<{ default: ComponentType<unknown> }>);
import { initErrorLogger } from "@/lib/errorLogger";
import { initializeFirstPartyAnalytics, trackEvent } from "@/lib/analytics";
import { isTrackingEnabled } from "@/lib/tracking-toggles";
import { Loader2 } from "lucide-react";
// PWA Install banner removed from here — it's in the Footer instead

// Lazy load pages for code splitting (lazyWithRetry for user-facing, lazy for admin)
const Index = lazyWithRetry(() => import("./pages/Index"));
const HowItWorks = lazyWithRetry(() => import("./pages/HowItWorks"));
const Generate = lazyWithRetry(() => import("./pages/Generate"));
const Playlists = lazyWithRetry(() => import("./pages/Playlists"));
const PlaylistDetail = lazyWithRetry(() => import("./pages/PlaylistDetail"));
const PlaylistGeneratorLanding = lazyWithRetry(() => import("./pages/PlaylistGeneratorLanding"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const VenueWaitlist = lazyWithRetry(() => import("./pages/VenueWaitlist"));
const BuyCoins = lazyWithRetry(() => import("./pages/BuyCoins"));
const GiftCoins = lazyWithRetry(() => import("./pages/GiftCoins"));
const PurchaseHistory = lazyWithRetry(() => import("./pages/PurchaseHistory"));
const ClaimGift = lazyWithRetry(() => import("./pages/ClaimGift"));
const CoinsPurchaseSuccess = lazyWithRetry(() => import("./pages/CoinsPurchaseSuccess"));
const CoinsHistory = lazyWithRetry(() => import("./pages/CoinsHistory"));
const Account = lazyWithRetry(() => import("./pages/Account"));
const MusicProfile = lazyWithRetry(() => import("./pages/MusicProfile"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Callback = lazyWithRetry(() => import("./pages/Callback"));
const Embed = lazyWithRetry(() => import("./pages/Embed"));
const Admin = lazy(() => import("./pages/Admin"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
// Payments page removed — redirects to /buy-coins (was duplicate)
const PaymentsMonitor = lazy(() => import("./pages/PaymentsMonitor"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const BlogAdmin = lazy(() => import("./pages/BlogAdmin"));
const BugReportsAdmin = lazy(() => import("./pages/BugReportsAdmin"));
const CuratorAccounts = lazy(() => import("./pages/CuratorAccounts"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const ClientErrors = lazy(() => import("./pages/ClientErrors"));
const AdminSentry = lazy(() => import("./pages/AdminSentry"));
const PerfMonitor = lazy(() => import("./pages/PerfMonitor"));
const AdminScripts = lazy(() => import("./pages/AdminScripts"));
const OnboardingPreview = lazy(() => import("./pages/OnboardingPreview"));
const AdminTracking = lazy(() => import("./pages/AdminTracking"));
const AdminStrategy = lazy(() => import("./pages/AdminStrategy"));
const BulkPlaylistManagement = lazy(() => import("./pages/BulkPlaylistManagement"));
const HomepageCarousel = lazy(() => import("./pages/HomepageCarousel"));
const AdminFeatured = lazy(() => import("./pages/AdminFeatured"));
const PlaylistGenerator = lazyWithRetry(() => import("./pages/PlaylistGenerator"));
const PlaylistMaker = lazyWithRetry(() => import("./pages/PlaylistMaker"));
const SpotifyPlaylistCreator = lazyWithRetry(() => import("./pages/SpotifyPlaylistCreator"));
const YouTubeMusicPlaylistGenerator = lazyWithRetry(() => import("./pages/YouTubeMusicPlaylistGenerator"));
const FreeAIPlaylistNoPremium = lazyWithRetry(() => import("./pages/FreeAIPlaylistNoPremium"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const AdminInvitations = lazy(() => import("./pages/AdminInvitations"));
const AdminInvoices = lazy(() => import("./pages/AdminInvoices"));
const ClaimInvite = lazyWithRetry(() => import("./pages/ClaimInvite"));
const GenerationMonitor = lazy(() => import("./pages/GenerationMonitor"));
const YouTubeConversions = lazy(() => import("./pages/YouTubeConversions"));
const AIModelConfig = lazy(() => import("./pages/AIModelConfig"));
const AdminReferrals = lazy(() => import("./pages/AdminReferrals"));
const CoinGenerationMonitor = lazy(() => import("./pages/CoinGenerationMonitor"));
const AdminWinback = lazy(() => import("./pages/AdminWinback"));
const InternalAnalytics = lazy(() => import("./pages/InternalAnalytics"));
const SpotifyToYoutube = lazyWithRetry(() => import("./pages/SpotifyToYoutube"));
const YoutubeToSpotify = lazyWithRetry(() => import("./pages/YoutubeToSpotify"));
const MyVibe = lazyWithRetry(() => import("./pages/MyVibe"));
const Roast = lazyWithRetry(() => import("./pages/Roast"));
const Community = lazyWithRetry(() => import("./pages/Community"));
const StagingTerminal = lazy(() => import("./pages/staging/StagingTerminal"));
const StagingVinyl = lazy(() => import("./pages/staging/StagingVinyl"));
const StagingCompact = lazy(() => import("./pages/staging/StagingCompact"));
const StagingGenreExplorer = lazy(() => import("./pages/staging/StagingGenreExplorer"));
const StagingPdp = lazy(() => import("./pages/staging/StagingPdp"));
const StagingResults = lazy(() => import("./pages/staging/StagingResults"));
const StagingAudition = lazy(() => import("./pages/staging/StagingAudition"));
const StagingBubbleGame = lazy(() => import("./pages/staging/StagingBubbleGame"));
const GenreExplorer = lazyWithRetry(() => import("./pages/GenreExplorer"));

const SWUpdater = () => { useServiceWorker(); useServiceWorkerUpdate(); return null; };

/** Scroll to top on route change */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/**
 * Restore body interactivity on every route change.
 *
 * Radix modal-layer primitives (Dialog, DropdownMenu, Popover) set
 * `document.body.style.pointerEvents = 'none'` while open and restore it on
 * close. When such a layer unmounts *during navigation* — e.g. clicking
 * "Remix" inside the playlist modal's dropdown, which navigates to /generate
 * before the layer's close cleanup runs — the inline style leaks, leaving the
 * destination page with every click silently swallowed and no visible overlay.
 * Clearing the stuck inline value on each pathname change neutralizes the whole
 * class of "navigate while a modal/dropdown is open" freezes. A legitimately
 * open modal on the new route re-asserts its own lock continuously, so this is
 * safe. (2026-06-04 — frozen /generate after Remix.)
 */
function BodyInteractivityGuard() {
  const location = useLocation();
  // Key on location.key (unique per history entry), NOT pathname: a modal can
  // navigate to the SAME path with new query/state (e.g. /playlists ->
  // /playlists?openPlaylist=ID) — pathname wouldn't change but the lock still
  // leaks. location.key changes on every navigation, so this fires for all of
  // them. (Does not cover a modal unmounted by conditional render with NO
  // navigation — that's fixed at the call site by closing before unmount.)
  useEffect(() => {
    if (document.body.style.pointerEvents === "none") {
      document.body.style.pointerEvents = "";
    }
  }, [location.key]);
  return null;
}

/**
 * Carry an expired/invalid auth link to the screen that can fix it.
 *
 * Supabase appends the reason to the URL fragment
 * (#error=access_denied&error_code=otp_expired&…) on the redirect target. Magic
 * link and signup confirmation both redirect to "/", where nothing reads it, so
 * the user landed on the homepage with no idea why their link didn't work — 55
 * magic links sent, and password recovery at 11 requests / 0 completions.
 * /auth renders the explanation and the "send a fresh one" form, so route there
 * and keep the fragment intact for it to parse.
 */
function AuthLinkErrorRedirect() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname === "/auth") return;
    const hash = window.location.hash?.startsWith("#") ? window.location.hash.slice(1) : "";
    if (!hash) return;
    const params = new URLSearchParams(hash);
    if (!params.get("error") && !params.get("error_code")) return;
    window.location.replace(`/auth${window.location.hash}`);
  }, [location.pathname]);
  return null;
}

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center space-y-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// QueryClientProvider + @tanstack/react-query removed 2026-05-08. There was
// exactly one useQuery() call in the entire repo (StatsBar) and the dep was
// pulling ~33KB raw / ~10KB gzip into the query-vendor chunk that loaded on
// every route via this provider. StatsBar now uses native useState +
// useEffect + sessionStorage cache for the same 15min staleTime behavior.

// Component to initialize error logger
const ErrorLoggerInit = () => {
  useEffect(() => {
    if (!isTrackingEnabled('error-logger')) return;
    const cleanup = initErrorLogger();
    // 2026-08-02 (S16-AUD-127): removed a
    // `sessionStorage.removeItem('chunk-error-reload')` here. It was meant
    // to re-arm ErrorBoundary's auto-reload guard "on successful app load",
    // but a successful mount of the MAIN bundle is not evidence that lazy
    // chunks resolve — it is precisely what happens on the reload the guard
    // just triggered. So: chunk 404 -> reload -> this effect clears the flag
    // -> the same lazy import 404s again -> reload, with nothing left to
    // stop it. On a deploy that genuinely ships a bad chunk that is an
    // infinite reload loop. The guard is now monotonic for the life of the
    // session; the second occurrence gets ErrorBoundary's Reload card.
    // (It was also gated on the error-logger tracking toggle, so turning
    // error logging off silently changed reload behaviour.)
    return cleanup;
  }, []);
  return null;
};

// Component to initialize first-party analytics
const AnalyticsInit = () => {
  useEffect(() => {
    if (!isTrackingEnabled('first-party-analytics')) return;
    const cleanup = initializeFirstPartyAnalytics();
    return cleanup;
  }, []);
  return null;
};

// Track SPA route changes as page views (initializeFirstPartyAnalytics
// only fires one page_view at boot — SPA navigation was invisible).
const RouteTracker = () => {
  const location = useLocation();
  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname === prevPath.current) return;
    prevPath.current = location.pathname;
    if (!isTrackingEnabled('route-tracker')) return;
    trackEvent('page_view', 'page_view', location.pathname);
  }, [location.pathname]);
  return null;
};

// Shared layout: renders Header + page content (via Outlet) + Footer.
// The min-h-screen flex-col wrapper ensures Footer sticks to the bottom.
// Routes that should NOT have Header/Footer (e.g. Callback, InternalAnalytics)
// are placed outside this layout wrapper in the route tree.
//
// IMPORTANT — do NOT add `bg-background` here. The brand background is
// the 135° gradient applied at body level in `src/index.css` (purple →
// violet → dark, fixed-attachment). A solid `bg-background` on this
// min-h-screen wrapper paints OVER that gradient and flattens the
// whole site to a single near-black tone. Andy caught this regression
// 2026-05-13 PM after an earlier UI sweep mis-fix added it. If a
// specific route looks pale, fix THAT route's wrapper, not this one.
//
// `text-foreground` keeps descendant text on a readable token-driven
// color even when a page component doesn't paint its own background.
const Layout = () => (
  <div className="min-h-screen flex flex-col text-foreground">
    {/* SiteImprovementBanner is lazy + null-fallback so its chunk doesn't
        block the route's PageLoader Suspense boundary while it loads. The
        banner's first DB poll is also deferred via requestIdleCallback
        inside the component (see PR #257). */}
    <Suspense fallback={null}>
      <SiteImprovementBanner />
    </Suspense>
    <Header />
    <Outlet />
    <Footer />
  </div>
);

// Mount gates for the two globally-mounted lazy components. Why this exists:
// `lazy()` defers a chunk fetch until React renders the component. Before
// these gates, App.tsx mounted <GlobalSpotifyPlayer /> + <GlobalPlaylistModal />
// unconditionally inside a Suspense boundary, so React fetched both chunks on
// first paint of every route — even though both components return null when
// no playlist is playing / no modal is open. (The internal early-returns saved
// render work, not download work.) Gating on context state means visitors who
// never trigger these UIs — most landing/public-route traffic — never pay for
// the chunks. Combined raw save: ~13.5KB per cold page-load. Pairs with PR
// #251 which split PlaylistDetailModal + QRCodeDialog out of GlobalPlaylistModal.
// (Andy 2026-05-08 perf pass — Lighthouse "Reduce unused JavaScript" follow-up.)
const GlobalSpotifyPlayerGate = () => {
  const { currentPlaylist } = useSpotifyPlayerContext();
  if (!currentPlaylist?.spotify_playlist_id) return null;
  return <GlobalSpotifyPlayer />;
};

const GlobalPlaylistModalGate = () => {
  const { isOpen, playlistId } = usePlaylistModal();
  if (!isOpen || !playlistId) return null;
  return <GlobalPlaylistModal />;
};

// AICuratorChat returns null on /generate (where the FAB is suppressed —
// see ROUTES_WITH_HIDDEN_FAB in AICuratorChat.tsx). Pre-2026-05-08 the
// AICuratorChatWrapper chunk (~18KB raw) was still fetched on /generate
// because lazy() resolves on render. Gate the entire wrapper so /generate
// visitors never download the chunk.
const ROUTES_WITHOUT_FAB = new Set(["/generate"]);
const AICuratorChatGate = () => {
  const { pathname } = useLocation();
  if (ROUTES_WITHOUT_FAB.has(pathname)) return null;
  return <AICuratorChatWrapper />;
};

// First-time-user welcome sequence. AuthContext sets the
// `spotivibly:pending-welcome:{userId}` flag in localStorage when it detects
// a brand-new user (created_at and last_sign_in_at within 60s of each other).
// This mounter reads the flag and lazy-loads DashboardWelcome only when
// needed. Logged-out and returning users never download the welcome chunk.
// (Andy 2026-05-08 — wires PR #277's component to the real signup flow.)
const DashboardWelcome = lazy(() =>
  import("@/components/DashboardWelcome").then((m) => ({ default: m.DashboardWelcome })),
);
// Asked immediately after the slides. Separate chunk for the same reason as
// the welcome itself: returning users never download either.
const WelcomePlatformStep = lazy(() =>
  import("@/components/WelcomePlatformStep").then((m) => ({ default: m.WelcomePlatformStep })),
);
const WelcomeGenresStep = lazy(() =>
  import("@/components/WelcomeGenresStep").then((m) => ({ default: m.WelcomeGenresStep })),
);
const WELCOME_FLAG_PREFIX = "spotivibly:pending-welcome:";
const WelcomeMounter = () => {
  const { user } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);
  // "slides" -> the DashboardWelcome sequence, "platform" -> the one-question
  // preference step. The welcome flag is cleared only at the END of the
  // second, so a reload mid-flow replays from the slides rather than dropping
  // the question — it is asked once, and a half-finished first run should not
  // be the run that consumes that one chance.
  const [phase, setPhase] = useState<"slides" | "platform" | "genres">("slides");

  useEffect(() => {
    if (!user?.id) {
      setShowWelcome(false);
      return;
    }
    try {
      if (localStorage.getItem(`${WELCOME_FLAG_PREFIX}${user.id}`) === "true") {
        setShowWelcome(true);
      }
    } catch {
      // localStorage unavailable (private mode / SSR) — silently skip the
      // welcome rather than crash. User just doesn't see it.
    }
  }, [user?.id]);

  // Two questions after the slides. Each advances rather than finishing, and
  // both are skippable — the flag is cleared only at the end of the last one.
  const handleSlidesComplete = () => setPhase("platform");
  const handlePlatformDone = () => setPhase("genres");

  const handleComplete = () => {
    if (user?.id) {
      try {
        localStorage.removeItem(`${WELCOME_FLAG_PREFIX}${user.id}`);
      } catch {
        // ignore
      }
    }
    setShowWelcome(false);
  };

  if (!showWelcome) return null;
  return (
    <Suspense fallback={null}>
      {phase === "slides" && <DashboardWelcome onComplete={handleSlidesComplete} />}
      {phase === "platform" && <WelcomePlatformStep onDone={handlePlatformDone} />}
      {phase === "genres" && <WelcomeGenresStep onDone={handleComplete} />}
    </Suspense>
  );
};

const App = () => (
  <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
        <SpotifyPlayerProvider>
          <PlaylistModalProvider>
            <TooltipProvider delayDuration={200}>
            <ErrorLoggerInit />
            <AnalyticsInit />
            <RouteTracker />
            {/* Each tracker honors /admin/tracking. Init components self-gate
             *  in their useEffect; the package-provided components don't, so
             *  we conditional-render them. Reading isTrackingEnabled at
             *  module-init time freezes the value for the React tree's
             *  lifetime — flipping a toggle requires reload (matches the
             *  `requiresReload` flag in tracking-toggles.ts). */}
            {/* Analytics + tracking are lazy with null fallbacks - they
                render no visible UI, just fire beacons / inject scripts.
                Defers their JS off the critical render path so React can
                reach the route's hero element sooner. */}
            {isTrackingEnabled('vercel-analytics') && (
              <Suspense fallback={null}><Analytics /></Suspense>
            )}
            {isTrackingEnabled('vercel-speed-insights') && (
              <Suspense fallback={null}><SpeedInsights /></Suspense>
            )}
            <Toaster />
            <ScrollToTop />
            {/* App-wide on purpose. usePerformanceObserver has watched
                layout-shift for months from /playlists alone — a noindex route
                outside the top 30 organic pages — while the homepage, which
                CrUX puts at CLS 0.94, had no observer at all. */}
            <WebVitalsReporter />
            <BodyInteractivityGuard />
            <AuthLinkErrorRedirect />
            <SWUpdater />
            {isTrackingEnabled('managed-scripts') && (
              <Suspense fallback={null}><ManagedScripts /></Suspense>
            )}
            <Suspense fallback={null}><CookieConsent /></Suspense>
            {/* PWA Install banner is in the Footer */}
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Routes WITH Header + Footer (via Layout) */}
              <Route element={<Layout />}>
                <Route path="/" element={<Index />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/playlist-generator" element={<PlaylistGenerator />} />
                <Route path="/playlist-maker" element={<PlaylistMaker />} />
                <Route path="/spotify-playlist-creator" element={<SpotifyPlaylistCreator />} />
                <Route path="/youtube-music-playlist-generator" element={<YouTubeMusicPlaylistGenerator />} />
                <Route path="/free-ai-playlist-no-premium" element={<FreeAIPlaylistNoPremium />} />
                <Route path="/convert" element={<SpotifyToYoutube />} />
                <Route path="/convert/youtube-to-spotify" element={<YoutubeToSpotify />} />
                {/* The natural English URL - Google site-search and direct
                 *  type-ins land here. Redirect to the active /convert
                 *  route which is also what the nav uses. */}
                <Route path="/converter" element={<Navigate to="/convert" replace />} />
                {/* Legacy URLs - redirect to new paths.
                    These used to RENDER the converter components, which meant
                    /spotify-to-youtube and /convert were the same page on two
                    URLs, each self-canonicalising via the shell - textbook
                    duplicate content. Nothing internally links to the short
                    forms and the sitemap lists only the long ones, so they
                    redirect now, matching what this comment always claimed.
                    vercel.json carries the matching 301 for direct hits, which
                    is what crawlers and bookmarks get; these two only handle
                    in-app navigation. */}
                <Route path="/spotify-to-youtube" element={<Navigate to="/convert" replace />} />
                <Route path="/youtube-to-spotify" element={<Navigate to="/convert/youtube-to-spotify" replace />} />
                <Route path="/generate" element={<Generate />} />
                <Route path="/my-vibe" element={<MyVibe />} />
                <Route path="/roast" element={<Roast />} />
                <Route path="/playlists" element={<Playlists />} />
                <Route path="/community" element={<Community />} />
                <Route path="/playlists/:id" element={<PlaylistDetail />} />
                {/* SEO Landing Pages for genre/mood combinations */}
                <Route path="/create/:slug" element={<PlaylistGeneratorLanding />} />
                <Route path="/pricing" element={<Pricing />} />
                {/* Demand validation for a venue tier. No product behind it (2026-08-25). */}
                <Route path="/for-venues" element={<VenueWaitlist />} />
                <Route path="/buy-coins" element={<BuyCoins />} />
                <Route path="/coins-success" element={<CoinsPurchaseSuccess />} />
                <Route path="/coins-history" element={<CoinsHistory />} />
                <Route path="/gift-coins" element={<GiftCoins />} />
                <Route path="/purchase-history" element={<PurchaseHistory />} />
                <Route path="/claim-gift" element={<ClaimGift />} />
                <Route path="/account" element={<Account />} />
                <Route path="/profile" element={<MusicProfile />} />
                <Route path="/achievements" element={<GenreExplorer />} />
                <Route path="/settings" element={<AdminRoute><Settings /></AdminRoute>} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                {/* Admin routes with Header/Footer */}
                <Route path="/admin" element={<AdminRoute><Navigate to="/admin/blog" replace /></AdminRoute>} />
                <Route path="/admin/settings" element={<AdminRoute><Admin /></AdminRoute>} />
                <Route path="/admin/payments-monitor" element={<AdminRoute><PaymentsMonitor /></AdminRoute>} />
                <Route path="/admin/blog" element={<AdminRoute><BlogAdmin /></AdminRoute>} />
                <Route path="/admin/bug-reports" element={<AdminRoute><BugReportsAdmin /></AdminRoute>} />
                <Route path="/admin/curator-accounts" element={<AdminRoute><CuratorAccounts /></AdminRoute>} />
                <Route path="/admin/audit-logs" element={<AdminRoute><AuditLogs /></AdminRoute>} />
                <Route path="/admin/client-errors" element={<AdminRoute><ClientErrors /></AdminRoute>} />
                <Route path="/admin/sentry" element={<AdminRoute><AdminSentry /></AdminRoute>} />
                <Route path="/admin/perf" element={<AdminRoute><PerfMonitor /></AdminRoute>} />
                <Route path="/admin/scripts" element={<AdminRoute><AdminScripts /></AdminRoute>} />
                <Route path="/onboarding-preview" element={<AdminRoute><OnboardingPreview /></AdminRoute>} />
                <Route path="/admin/tracking" element={<AdminRoute><AdminTracking /></AdminRoute>} />
                <Route path="/admin/strategy" element={<AdminRoute><AdminStrategy /></AdminRoute>} />
                <Route path="/admin/bulk-playlists" element={<AdminRoute><BulkPlaylistManagement /></AdminRoute>} />
                <Route path="/admin/homepage-carousel" element={<AdminRoute><HomepageCarousel /></AdminRoute>} />
                <Route path="/admin/featured" element={<AdminRoute><AdminFeatured /></AdminRoute>} />
                <Route path="/admin/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
                <Route path="/admin/invitations" element={<AdminRoute><AdminInvitations /></AdminRoute>} />
                <Route path="/admin/invoices" element={<AdminRoute><AdminInvoices /></AdminRoute>} />
                <Route path="/invite/:token" element={<ClaimInvite />} />
                <Route path="/admin/generation-monitor" element={<AdminRoute><GenerationMonitor /></AdminRoute>} />
                <Route path="/admin/referrals" element={<AdminRoute><AdminReferrals /></AdminRoute>} />
                <Route path="/admin/ai-models" element={<AdminRoute><AIModelConfig /></AdminRoute>} />
                <Route path="/admin/coin-monitor" element={<AdminRoute><CoinGenerationMonitor /></AdminRoute>} />
                <Route path="/admin/winback" element={<AdminRoute><AdminWinback /></AdminRoute>} />
                <Route path="/admin/youtube-conversions" element={<AdminRoute><YouTubeConversions /></AdminRoute>} />
                <Route path="/staging/terminal" element={<StagingGate><StagingTerminal /></StagingGate>} />
                <Route path="/staging/vinyl" element={<StagingGate><StagingVinyl /></StagingGate>} />
                <Route path="/staging/compact" element={<StagingGate><StagingCompact /></StagingGate>} />
                <Route path="/genre-explorer" element={<StagingGate><StagingGenreExplorer /></StagingGate>} />
                <Route path="/staging/genre-explorer" element={<StagingGate><StagingGenreExplorer /></StagingGate>} />
                {/* Review harness for the real PlaylistDetailModal with mock
                    data. Gated like the other /staging/* routes. */}
                <Route path="/staging/pdp" element={<StagingGate><StagingPdp /></StagingGate>} />
                {/* Review harness for the real PlaylistResults screen, which only
                    exists after a live generation and was therefore unverifiable
                    locally. Gated like the other /staging/* routes. */}
                <Route path="/staging/results" element={<StagingGate><StagingResults /></StagingGate>} />
                {/* Phase 0 spike: "Tune by ear" audition mode over a real playlist. */}
                <Route path="/staging/audition" element={<StagingGate><StagingAudition /></StagingGate>} />
                {/* "Read your mind" bubble game -> grounded generation. */}
                <Route path="/staging/bubble-game" element={<StagingGate><StagingBubbleGame /></StagingGate>} />
                <Route path="/admin/insights" element={<AdminRoute><InternalAnalytics /></AdminRoute>} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Route>

              {/* Routes WITHOUT Header/Footer */}
              <Route path="/callback" element={<Callback />} />
              {/* Embeddable player - framed on other sites (e.g. FitMito).
                  Framing allowed via scoped CSP frame-ancestors in vercel.json. */}
              <Route path="/embed/:id" element={<Embed />} />

              {/* Redirects (no layout needed) */}
              <Route path="/analytics" element={<Navigate to="/admin/insights" replace />} />
              <Route path="/payments" element={<Navigate to="/buy-coins" replace />} />
              </Routes>
            </Suspense>
            {/* App chrome (curator chat FAB, global player, welcome) - suppressed
                on the embeddable /embed route, which must render as a pure
                player inside a host site's iframe. */}
            <ChromeGates />
            </TooltipProvider>
          </PlaylistModalProvider>
        </SpotifyPlayerProvider>
      </AuthProvider>
    </BrowserRouter>
  </ErrorBoundary>
);

export default App;

/** Global overlay mounts (curator chat FAB, player, welcome), hidden on the
 *  chrome-less /embed route. Rendered inside BrowserRouter so useLocation works. */
function ChromeGates() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/embed/")) return null;
  return (
    <Suspense fallback={null}>
      <AICuratorChatGate />
      <GlobalSpotifyPlayerGate />
      <GlobalPlaylistModalGate />
      <WelcomeMounter />
    </Suspense>
  );
}
